import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Alert,
  Link,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { useAuth } from '../auth/useAuth';
import { useNavigate } from 'react-router-dom';

const COUNTRY_CODES = [
  { code: '+1', label: '+1' },
  { code: '+44', label: '+44' },
  { code: '+61', label: '+61' },
  { code: '+49', label: '+49' },
  { code: '+33', label: '+33' },
  { code: '+971', label: '+971' },
  { code: '+966', label: '+966' },
  { code: '+353', label: '+353' },
  { code: '+64', label: '+64' },
  { code: '+27', label: '+27' },
];

interface SignupData {
  email: string;
  password: string;
  confirmPassword: string;
  clinicName: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  phone: string;
  termsAccepted: boolean;
}

interface SignupErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  clinicName?: string;
  firstName?: string;
  lastName?: string;
  termsAccepted?: string;
}

const Signup = () => {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [data, setData] = useState<SignupData>({
    email: '',
    password: '',
    confirmPassword: '',
    clinicName: '',
    firstName: '',
    lastName: '',
    countryCode: '+1',
    phone: '',
    termsAccepted: false,
  });
  const [errors, setErrors] = useState<SignupErrors>({});

  const validateForm = (): boolean => {
    const newErrors: SignupErrors = {};

    if (!data.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!data.password) {
      newErrors.password = 'Password is required';
    } else if (data.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(data.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and numbers';
    }

    if (data.password !== data.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!data.clinicName.trim()) {
      newErrors.clinicName = 'Practice name is required';
    }

    if (!data.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!data.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!data.termsAccepted) {
      newErrors.termsAccepted = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Same value the setup wizard sends, so a signup and a later re-run agree.
      const TERMS_VERSION = '1.0';
      const fullPhoneForSignup = data.phone ? `${data.countryCode} ${data.phone}` : undefined;
      const firstName = data.firstName.trim();
      const lastName = data.lastName.trim();
      await signup(data.email, data.password, {
        clinicName: data.clinicName.trim(),
        firstName,
        lastName,
        phone: fullPhoneForSignup,
        termsAccepted: data.termsAccepted,
        termsVersion: TERMS_VERSION,
      });

      const fullPhone = data.phone ? `${data.countryCode} ${data.phone}` : '';

      // localStorage (not sessionStorage): email verification now sits between
      // signup and onboarding, and clicking the verification link can open a new
      // tab — sessionStorage is per-tab and would be lost there. localStorage is
      // shared across same-origin tabs; the wizard removes it on completion.
      localStorage.setItem(
        'onboardingUserData',
        JSON.stringify({
          firstName,
          lastName,
          phone: fullPhone,
          email: data.email,
          name: `${firstName} ${lastName}`,
        })
      );

      // Verify email before onboarding — the wall resumes to /onboarding once
      // the link is clicked (onboardingUserData stays in sessionStorage).
      navigate('/verify-pending');
    } catch (err: any) {
      console.error('Signup error:', err);
      setError(
        err.response?.data?.message ||
        err.message ||
        'An error occurred during signup. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof SignupData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setData({
      ...data,
      [field]: field === 'termsAccepted' ? e.target.checked : e.target.value,
    });
    if (errors[field as keyof SignupErrors]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left Panel */}
      <Box
        sx={{
          flex: isMobile ? 1 : '0 0 50%',
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 3, sm: 6, md: 8 },
          py: 3,
          overflow: 'auto',
        }}
      >
        {/* Top bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box
            component="img"
            src="/stabletrack-logo-horizontal.png"
            alt="StableTrack"
            sx={{ height: 32 }}
          />
          <IconButton onClick={() => navigate('/login')} size="small">
            <ArrowBack fontSize="small" />
          </IconButton>
        </Box>

        {/* Form content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            maxWidth: 400,
            mx: 'auto',
            width: '100%',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Create a clinic account
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            You're almost there! Create your new account by completing these details.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Practice name
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter your practice name"
              value={data.clinicName}
              onChange={handleChange('clinicName')}
              error={!!errors.clinicName}
              helperText={errors.clinicName}
              sx={{ mb: 2 }}
              size="medium"
            />

            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              First name
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter your first name"
              value={data.firstName}
              onChange={handleChange('firstName')}
              error={!!errors.firstName}
              helperText={errors.firstName}
              sx={{ mb: 2 }}
              size="medium"
            />

            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Last name
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter your last name"
              value={data.lastName}
              onChange={handleChange('lastName')}
              error={!!errors.lastName}
              helperText={errors.lastName}
              sx={{ mb: 2 }}
              size="medium"
            />

            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Email
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter your email address"
              type="email"
              value={data.email}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              sx={{ mb: 2 }}
              size="medium"
            />

            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Password
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter a password"
              type={showPassword ? 'text' : 'password'}
              value={data.password}
              onChange={handleChange('password')}
              error={!!errors.password}
              helperText={errors.password}
              sx={{ mb: 2 }}
              size="medium"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Confirm password
            </Typography>
            <TextField
              fullWidth
              placeholder="Confirm your password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={data.confirmPassword}
              onChange={handleChange('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              sx={{ mb: 2 }}
              size="medium"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      size="small"
                    >
                      {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Mobile number
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Select
                value={data.countryCode}
                onChange={(e) => setData({ ...data, countryCode: e.target.value as string })}
                size="medium"
                sx={{ minWidth: 90 }}
              >
                {COUNTRY_CODES.map((cc) => (
                  <MenuItem key={cc.code} value={cc.code}>
                    {cc.label}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                fullWidth
                placeholder="Enter your mobile number"
                value={data.phone}
                onChange={handleChange('phone')}
                size="medium"
              />
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={data.termsAccepted}
                  onChange={handleChange('termsAccepted')}
                  size="small"
                  sx={{ color: '#C9772B', '&.Mui-checked': { color: '#C9772B' } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  I agree to the{' '}
                  <Link href="/privacy" target="_blank" rel="noopener" sx={{ color: '#C9772B' }}>
                    Privacy Policy
                  </Link>
                  ,{' '}
                  <Link href="/terms" target="_blank" rel="noopener" sx={{ color: '#C9772B' }}>
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link href="/terms" target="_blank" rel="noopener" sx={{ color: '#C9772B' }}>
                    Terms of Business
                  </Link>
                  .
                </Typography>
              }
              sx={{ mb: 1, alignItems: 'flex-start', mt: 0.5 }}
            />
            {errors.termsAccepted && (
              <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                {errors.termsAccepted}
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 2,
                bgcolor: '#C9772B',
                '&:hover': { bgcolor: '#6B4727' },
                py: 1.5,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 1.5,
              }}
            >
              {loading ? 'Creating account...' : 'Create account'}
            </Button>
          </Box>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Already have an account?{' '}
              <Link
                component="button"
                onClick={() => navigate('/login')}
                sx={{
                  textDecoration: 'none',
                  color: '#C9772B',
                  fontWeight: 600,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Sign in
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Right Panel - Horse Image */}
      {!isMobile && (
        <Box
          sx={{
            flex: '0 0 50%',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            component="img"
            src="/a-beautiful-horse-stands-in-a-paddock-behind-a-woo.jpg"
            alt="Horses in paddock"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 5,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
            }}
          >
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
              Modern practice management.
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              Everything you need to run your equine clinic efficiently, from scheduling to billing and clinical records.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Signup;
